import type { AdminUser } from '../../generated/api/types.gen.js';
import type { BadgeVariant } from '../../components/ui/Badge.js';

type UserStatus = AdminUser['status'];

export const USER_STATUS_LABEL: Record<UserStatus, string> = {
  pending: 'Pending approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const USER_STATUS_VARIANT: Record<UserStatus, BadgeVariant> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
};

/** Roles an admin can assign or invite (generated `UserRoleAssign['role']` without code_reviewer). */
export const ASSIGNABLE_ROLES = [
  { value: 'viewer', label: 'Viewer', description: 'Read-only access to dashboards and data' },
  { value: 'analyst', label: 'Analyst', description: 'AI agents, workflows and research tools' },
  { value: 'trader', label: 'Trader', description: 'Run trades and manage strategies' },
  { value: 'org_admin', label: 'Organization admin', description: 'Full control of the organization' },
] as const;

const ROLE_LABEL: Record<string, string> = {
  superadmin: 'Superadmin',
  org_admin: 'Organization admin',
  trader: 'Trader',
  analyst: 'Analyst',
  code_reviewer: 'Code reviewer',
  viewer: 'Viewer',
  user: 'User',
};

export function roleLabel(role: string): string {
  return ROLE_LABEL[role] ?? role.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

export function roleBadgeVariant(role: string): BadgeVariant {
  switch (role) {
    case 'superadmin':
      return 'purple';
    case 'org_admin':
      return 'info';
    case 'trader':
      return 'success';
    case 'analyst':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function userDisplayName(user: Pick<AdminUser, 'fullName' | 'username' | 'email'>): string {
  return user.fullName || user.username || user.email;
}

export function formatDate(value: string | null | undefined, empty = 'Never'): string {
  if (!value) return empty;
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
